// src/contexts/MessageSocketContext.js
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBaseSocket } from './BaseSocketContext';

const MessageSocketContext = createContext();

export const useMessageSocket = () => {
    return useContext(MessageSocketContext);
};

export const MessageSocketProvider = ({ children }) => {
    const { socket, connected } = useBaseSocket();
    const location = useLocation();
    const [messageSound] = useState(new Audio('/sounds/message.mp3'));

    // Memoized message handlers to prevent unnecessary re-renders
    const messageHandlers = useMemo(() => ({
        handleWhatsAppMessage: (data) => {

            // Only show toast and play sound if not on the chat page
            const isOnChatPage = location.pathname.includes('/chat');
            if (!isOnChatPage) {
                // Extract message data - handle both array and object formats
                let messageData;

                // Check if data is an array
                if (Array.isArray(data) && data.length > 0) {
                    messageData = data[0]?.message || {};
                } else {
                    // Assume data is an object with a message property
                    messageData = data?.message || {};
                }


                // Get sender information
                const senderName = messageData.from?.name || 'Unknown';
                const senderPhone = messageData.from?.phone || '';
                const displayName = senderName !== 'Unknown' ? senderName : senderPhone;

                // Get message content based on type
                let messageContent = '';
                let messageIcon = null;


                switch (messageData.type) {
                    case 'text_message':
                        messageContent = messageData.content?.text || 'New message';
                        break;
                    case 'media_message':
                        // Handle media messages based on mediaType
                        const mediaType = messageData.content?.mediaType || '';
                        switch (mediaType) {
                            case 'image':
                                messageContent = '📷 Image';
                                messageIcon = '📷';
                                break;
                            case 'video':
                                messageContent = '🎬 Video';
                                messageIcon = '🎬';
                                break;
                            case 'audio':
                                messageContent = '🎵 Audio';
                                messageIcon = '🎵';
                                break;
                            case 'document':
                                messageContent = '📄 Document';
                                messageIcon = '📄';
                                break;
                            case 'sticker':
                                messageContent = '🙂 Sticker';
                                messageIcon = '🙂';
                                break;
                            default:
                                messageContent = `📁 Media (${mediaType})`;
                                messageIcon = '📁';
                        }
                        break;
                    case 'image_message':
                        messageContent = '📷 Image';
                        messageIcon = '📷';
                        break;
                    case 'video_message':
                        messageContent = '🎬 Video';
                        messageIcon = '🎬';
                        break;
                    case 'audio_message':
                        messageContent = '🎵 Audio';
                        messageIcon = '🎵';
                        break;
                    case 'document_message':
                        messageContent = '📄 Document';
                        messageIcon = '📄';
                        break;
                    case 'location_message':
                        messageContent = '📍 Location';
                        messageIcon = '📍';
                        break;
                    case 'contact_message':
                        messageContent = '👤 Contact';
                        messageIcon = '👤';
                        break;
                    case 'sticker_message':
                        messageContent = '🙂 Sticker';
                        messageIcon = '🙂';
                        break;
                    default:
                        // Log the unknown message type for debugging
                        messageContent = `New message (${messageData.type || 'unknown type'})`;
                }

                // Show toast notification
                toast.info(
                    <div className="flex flex-col">
                        <div className="font-bold text-sm">{displayName}</div>
                        <div className="flex items-center">
                            {messageIcon && <span className="mr-2">{messageIcon}</span>}
                            <span className="text-sm truncate">{messageContent}</span>
                        </div>
                    </div>,
                    {
                        position: "top-right",
                        autoClose: 5000,
                        onClick: () => {
                            // Navigate to chat page on click
                            window.location.href = '/chat';
                        }
                    }
                );

                // Play notification sound
                try {
                    messageSound.volume = 0.5;
                    messageSound.play().catch(() => {});
                } catch (err) {
                    console.error('Error playing notification sound:', err);
                }
            }
        },

        handleMessageStatus: (update) => {
            // Handle message status updates
        },

        handleTemplateStatusUpdate: (update) => {
            // Handle template status updates
        }
    }), [location.pathname, messageSound]);

    // Set up message-related socket listeners
    useEffect(() => {
        if (!socket || !connected) return;

        // Listen for WhatsApp message received events
        socket.on('whatsapp_message_received', messageHandlers.handleWhatsAppMessage);

        // Listen for message status updates
        socket.on('message_status', messageHandlers.handleMessageStatus);

        // Listen for template status updates
        socket.on('template_status_update', messageHandlers.handleTemplateStatusUpdate);

        // Clean up listeners
        return () => {
            socket.off('whatsapp_message_received', messageHandlers.handleWhatsAppMessage);
            socket.off('message_status', messageHandlers.handleMessageStatus);
            socket.off('template_status_update', messageHandlers.handleTemplateStatusUpdate);
        };
    }, [socket, connected, messageHandlers]);

    // Helper methods for message handling
    const onMessage = (handler) => {
        if (socket) {
            socket.on('whatsapp_message_received', handler);
        }
    };

    const onAgentMessageSent = (handler) => {
        if (socket) {
            socket.on('agent_message_sent', handler);
        }
    };

    const onStatusUpdate = (handler) => {
        if (socket) {
            socket.on('message_status_update', handler);
        }
    };

    const off = (event, handler) => {
        if (socket) {
            socket.off(event, handler);
        }
    };

    const value = {
        onMessage,
        onAgentMessageSent,
        onStatusUpdate,
        off
    };

    return (
        <MessageSocketContext.Provider value={value}>
            {children}
        </MessageSocketContext.Provider>
    );
};

export default MessageSocketContext;